// B10 Trip book (lane 4's mockup docs/renders/trip-book tb02–tb04): a trip's recap as a book with
// pages that turn. The cover (the trip, who came, four totals), a page per stage (the places found,
// the acts opened on its island, its collectibles, the holiday tasks done there) and the last page
// (who found what, the best moments, plan the next trip). Opened from Crate › Past trips, for the
// trip under way too (a recap so far), and from the books on the Crate's shelf (Crate › Books, in
// 3D on the world stage, flat spines in Calm/Still).
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { Person } from '../../shared/events'
import { figureFor } from '../../shared/figures'
import type { Pack } from '../../shared/pack/schema'
import { closeOverlay, openOverlay } from '../app/router'
import { useModal } from '../app/Sheet'
import { figureBg, FigureIcon } from '../art/FigureIcon'
import { db } from '../data/db'
import { packFor, usePack } from '../data/pack'
import { useSession } from '../data/session'
import { tripBook } from '../domain/tripBook'
import { fmt, t } from '../strings/en'
import { useTrips } from './PastTripsSheet'

const COVER: Record<string, string> = { blue: '#2F7BFF', snow: '#5E7189', teal: '#43C6DB', mint: '#36D399', yellow: '#E0A21A', red: '#FF4F4F' }
const RARE_IC: Record<string, string> = { legendary: 'var(--yellow)', rare: 'var(--purple, #9B6BFF)', common: 'var(--ice)' }

function Island({ colour }: { colour: string }) {
  return (
    <svg viewBox="0 0 340 150" aria-hidden>
      <path d="M30,120 L120,40 L170,80 L230,30 L320,120 Z" fill="#fff" stroke="#1B2A3A" strokeWidth="3.5" />
      <rect x="20" y="118" width="300" height="20" rx="8" fill="#E8F2FF" stroke="#1B2A3A" strokeWidth="3.5" />
      <g fill="#2F9E62" stroke="#1B2A3A" strokeWidth="2.5">
        <path d="M60,118 l10,-24 l10,24z" />
        <path d="M84,118 l9,-20 l9,20z" />
        <path d="M262,118 l10,-24 l10,24z" />
      </g>
      <circle cx="150" cy="96" r="9" fill={colour} stroke="#1B2A3A" strokeWidth="2.5" />
    </svg>
  )
}

export function TripBookScreen({ person, tripId }: { person: Person; tripId: string }) {
  const active = usePack((s) => s.pack)
  const first = usePack((s) => s.firstTrip) ?? tripId
  const trips = useTrips()
  const everyone = useSession((s) => s.people)
  const people = useMemo(() => everyone ?? [], [everyone])
  const events = useLiveQuery(() => db.events.toArray(), [], [])
  // The trip under way is the loaded pack; a past one is fetched (then cached by the service worker).
  const isActive = active?.meta.id === tripId
  const [fetched, setFetched] = useState<Pack | null>(null)
  useEffect(() => {
    const trip = isActive ? undefined : trips?.find((x) => x.id === tripId)
    if (trip) void packFor(trip).then(setFetched, () => setFetched(null))
  }, [isActive, trips, tripId])
  const pack = isActive ? active : fetched
  const book = useMemo(() => (pack ? tripBook(events, tripId, first, pack, people.map((p) => ({ id: p.id, name: p.name }))) : null), [events, tripId, first, pack, people])
  const [page, setPage] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const headId = useId()
  useModal(ref, { onClose: closeOverlay, active: !!book })
  if (!book || !pack) return null

  const n = book.pages.length + 2
  const at = Math.min(page, n - 1)
  const stage = at > 0 && at < n - 1 ? book.pages[at - 1]! : null
  const colour = (stageId: string) => COVER[pack.itinerary.stages.find((s) => s.id === stageId)?.color ?? 'blue'] ?? '#2F7BFF'
  const names = book.people.filter((p) => people.find((x) => x.id === p.id)?.kind === 'child').map((p) => p.name)

  return (
    <div ref={ref} className="trip-book" role="dialog" aria-modal="true" tabIndex={-1} aria-labelledby={headId}>
      <div className="book-head">
        <button className="pill press" onClick={closeOverlay}>
          {t.book.shelf}
        </button>
        <span className="pill pill-yellow">{stage ? t.book.stageTag(at, stage.name) : at === 0 ? book.title : t.book.theEnd}</span>
      </div>

      {at === 0 ? (
        <section className="book-page cover" data-page="cover">
          <div className="mono">{t.book.ourBook}</div>
          <h2 id={headId}>{book.title}</h2>
          <div className="sub">{t.book.dates(fmt.tripDates(book.start, book.end), book.islands)}</div>
          <Island colour="#FFC93C" />
          <div className="book-figs">
            {book.people.map((p) => {
              const who = people.find((x) => x.id === p.id)
              if (!who) return null
              const look = figureFor(who, people)
              return (
                <span key={p.id} className="fig" style={{ background: figureBg(look) }} title={p.name}>
                  <FigureIcon figure={look} size={44} />
                </span>
              )
            })}
          </div>
          <div className="sub">{names.length ? t.book.who(names) : book.people.map((p) => p.name).join(' · ')}</div>
          <div className="book-stats">
            <div>
              <b>{book.totals.places}</b>
              <small>{t.book.places}</small>
            </div>
            <div>
              <b>{book.totals.acts}</b>
              <small>{t.book.acts(book.totals.rare)}</small>
            </div>
            <div>
              <b>{book.totals.words}</b>
              <small>{t.book.words}</small>
            </div>
            <div>
              <b>{book.totals.collectibles}</b>
              <small>{t.book.collectibles(book.totals.prizes)}</small>
            </div>
          </div>
        </section>
      ) : stage ? (
        <section className="book-page" data-page={`stage:${stage.stageId}`}>
          <h2 id={headId}>{stage.name}</h2>
          <div className="sub">{t.book.stageDates(fmt.tripDates(stage.start, stage.end), stage.days)}</div>
          <div className="book-island">
            <Island colour={colour(stage.stageId)} />
            {stage.checkins ? <span className="stamp">{t.book.checkedIn(stage.checkins)}</span> : null}
          </div>
          <div className="book-list">
            <div className="item">
              <span className="ic" style={{ background: 'var(--yellow)' }}>📍</span>
              <span>
                <b>{t.book.found}</b> {stage.found.length ? stage.found.join(', ') : t.book.nothingYet}
              </span>
            </div>
            {stage.acts.slice(0, 3).map((a) => (
              <div className="item" key={`${a.act}-${a.by}`}>
                <span className="ic" style={{ background: RARE_IC[a.rarity] }}>★</span>
                <span>
                  <b>{a.rarity === 'common' ? t.book.act : t.book.rareAct}</b> {t.book.actBy(a.label, people.find((p) => p.id === a.by)?.name ?? '')}
                </span>
              </div>
            ))}
            {stage.tasks.map((x) => (
              <div className="item" key={x}>
                <span className="ic" style={{ background: 'var(--mint)' }}>✓</span>
                <span>
                  <b>{t.book.task}</b> {x}
                </span>
              </div>
            ))}
          </div>
          {stage.collectibles.length ? (
            <>
              <div className="mono book-sub">{t.book.collectiblesFrom(stage.name)}</div>
              <div className="book-charms">
                {stage.collectibles.map((c) => (
                  <span key={c.id} className="charm" title={c.name}>
                    {c.art ? <img src={c.art} alt={c.name} /> : c.emoji}
                  </span>
                ))}
              </div>
            </>
          ) : null}
        </section>
      ) : (
        <section className="book-page end" data-page="end">
          <h2 id={headId}>{t.book.whatATrip}</h2>
          <div className="sub">{t.book.whoFound}</div>
          <div className="book-who">
            {book.who.map((p) => (
              <div key={p.id}>
                <b>{p.name}</b>
                <small>{t.book.personLine(p.places, p.acts, p.words, p.collectibles, p.best)}</small>
              </div>
            ))}
          </div>
          {book.best.length ? (
            <>
              <div className="mono book-sub">{t.book.best}</div>
              <div className="book-list">
                {book.best.map((a) => (
                  <div className="item" key={`${a.place}-${a.act}`}>
                    <span className="ic" style={{ background: RARE_IC[a.rarity] }}>★</span>
                    <span>{a.label}</span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
          {person.kind === 'admin' ? (
            <button className="btn y press" onClick={() => openOverlay('new-trip')}>
              {t.book.next}
            </button>
          ) : null}
          <button className="btn w press" onClick={closeOverlay}>
            {t.book.backToShelf}
          </button>
        </section>
      )}

      <div className="book-turn" role="group" aria-label={t.book.pages}>
        <button className="arrow press" aria-label={t.book.prev} disabled={at === 0} onClick={() => setPage(at - 1)}>
          ‹
        </button>
        {Array.from({ length: n }, (_, i) => (
          <i key={i} className={i === at ? 'on' : undefined} aria-hidden />
        ))}
        <button className="arrow press" aria-label={t.book.next_} disabled={at === n - 1} onClick={() => setPage(at + 1)}>
          ›
        </button>
      </div>
    </div>
  )
}
